import { el, btn, backButton, MENU_ICONS } from '@/ui/dom';
import { brandLogo } from '@/ui/brandLogo';
import { sfxUiTick } from '@/audio/sfx';
import { CROWN_SVG, CHEV_SVG } from '@/ui/lockCta';
import { CITIES } from '@/config/cities';
import { VEHICLE_ROSTER } from '@/config/racers';
import type { AccountState } from '@/account/AccountService';

/**
 * PurchaseScreen.ts — the paid gate's front door (M121; redesigned M123 to the
 * black-and-gold cinema standard: a price lockup hero, real city photography
 * in the M112 fanned-card language, icon chips instead of bullet paragraphs,
 * and a celebration-worthy success face).
 *
 * Faces:
 *   pitch      — the sell. M147 (owner reference art "New Paywall Screen"):
 *                full-bleed over the owner's rooftop photo (public/paywall/
 *                bg.webp) — the logo art (M148), a chrome + gold two-line
 *                headline, three feature columns, the big gold price with the
 *                one-time promise, the gold BUY CTA and the SIGN IN button,
 *                then the license disclosure BELOW the buttons (owner-approved
 *                placement) with a one-line license notice pinned directly
 *                under the primary CTA.
 *   busy       — handing off to Stripe checkout.
 *   confirming — returned from payment; the webhook grant is not visible yet.
 *                Calm and premium: never an error for slowness.
 *   success    — the player owns the game. A fresh purchase (arrived via
 *                confirming/busy) gets the gold celebration; an already-owned
 *                visitor gets the calmer FULL GAME · OWNED variant.
 *   error      — checkout could not start. "Nothing was charged" stays.
 *
 * Built in the AccountScreen idiom: shell once in the constructor, render()
 * repaints faces, the back chevron lives on the ROOT so render() cannot
 * destroy it (M114b). M92 colour rule — GOLD is the affirmative action and
 * NOTHING here is red, because nothing here is destructive.
 *
 * THE LICENSE DISCLOSURE (owner-locked 2026-08-13, see docs/PAID-LAUNCH-PLAN.md):
 *  - Shown to EVERY buyer GLOBALLY, not geo-targeted to California/Maryland.
 *    Geolocation would add a failure mode where the notice does not render for
 *    someone who needed it, and a third state passing an equivalent law (New
 *    York has a bill introduced) would mean re-doing it.
 *  - NOTICE ONLY. There is deliberately NO acknowledgment checkbox — the owner
 *    declined it after the Maryland exposure was explained, and the enrolled
 *    text of Maryland HB 208 reads "or" rather than "and", so a conspicuous
 *    statement plus a link to full terms appears to satisfy both states. DO NOT
 *    add a checkbox back without asking; it was a decision, not an oversight.
 *  - M123 placement (owner-approved): a short license line sits DIRECTLY under
 *    the primary CTA, and the full four-point block sits below the buttons in
 *    its own bordered treatment — visible without interaction, never a
 *    collapsible. Do not restyle it into the page background; the visual
 *    separation is part of what makes it "clear and conspicuous".
 *  - NO offline/single-player claim in the disclosure. The strong version
 *    ("single player is not server dependent") must be LITERALLY TRUE of the
 *    build it ships on or it is precisely the sentence a plaintiff quotes
 *    back, and the web build is served from Pages behind an entitlement
 *    check. Deliberately omitted (M123 spec) — do not re-add unverified.
 */

export interface PurchaseCallbacks {
  /** Start checkout. Resolves to an error message, or null when it navigated. */
  onBuy: () => Promise<string | null>;
  /** Signed out: create / sign in to a Racer ID first. */
  onSignIn: () => void;
  /** Play the demo race instead (the error face's escape). Omitted when no
   *  teaser is reachable. */
  onTeaser?: () => void;
  /** Open the full Terms of Sale and License. */
  onTerms: () => void;
  /** Leave the screen — always provided now (dismissible back to title). */
  onBack?: () => void;
  /** Success face CTA — straight into the game. */
  onPlay?: () => void;
}

export interface PurchaseInfo {
  /** Display price, already formatted (e.g. "$9.99"). */
  price: string;
  /** True once the account layer has RESOLVED this player as an owner. */
  owned: boolean;
  /** Set when a previous purchase was refunded — shown calmly, not as an error. */
  refundedOn?: string;
  /** Returned from Stripe checkout; the entitlement grant is not visible yet. */
  confirming?: boolean;
  /** What brought the player here. M147: the pitch shows one fixed sell, so
   *  this no longer changes the screen; Flow still records it. */
  context?: 'gate' | 'cap' | 'return';
  /** Home-modernize round (owner: "a CTA to buy the game to unlock" on every
   *  paywalled thing): the locked FEATURE the player tapped. M147: no longer
   *  shown on the pitch (one fixed headline, owner reference art). */
  feature?: PaywallFeature;
}

/** The paywalled surfaces a non-owner can tap into. */
export type PaywallFeature = 'tour' | 'online' | 'shop' | 'cities' | 'garage' | 'crew';

/** The benefit line per locked feature — the lock banner a paywalled page
 *  wears (Flow.paywallLock). No em dashes, the house rule. M147: the paywall
 *  itself no longer names the feature (the owner's reference art carries one
 *  fixed headline), so only the line remains. */
export const FEATURE_PITCH: Record<PaywallFeature, { sub: string }> = {
  tour: { sub: 'RACE ALL 9 CITIES FOR THE CUP' },
  online: { sub: 'RACE REAL PLAYERS AROUND THE WORLD' },
  shop: { sub: 'PAINT JOBS, WHEELS AND MODS FOR YOUR RIDE' },
  cities: { sub: 'QUICK RACE ALL 9 CITIES, ANY TIME' },
  // Owner rule: buying doesn't unlock the rides — winning races does.
  garage: { sub: 'WIN RACES TO EARN XP AND UNLOCK EVERY RIDE' },
  // M140 (owner: clubs are for game owners on the web). Racer Club since M141.
  crew: { sub: 'START A CLUB AND RACE YOUR FRIENDS' },
};

// ---- copy (no em dashes in player-facing copy — house rule) ----------------

/** M147 — the owner's reference-art copy, verbatim except two words he
 *  approved changing (2026-10-06): "OWN THE GAME." became "GET THE GAME." and
 *  "FULL GAME FOREVER" became "FREE UPDATES". The license notice below says
 *  "not ownership of the game", and "play forever" was retired in the launch
 *  plan (docs/PAID-LAUNCH-PLAN.md decision 4) — an ownership/forever headline
 *  sitting above that notice is the "cumulative impression" the California
 *  AB 2426 suits attack. Do not bring either word back without asking. */
const HEADLINE = ['GET THE GAME.', 'WIN THE CUP!'] as const;
const ONE_TIME_LINE = 'ONE-TIME PURCHASE.';
const PROMISE_LINE = 'NO SUBSCRIPTION. FREE UPDATES.';

/** The three feature columns (title + sub, two lines each, broken exactly
 *  where the reference art breaks them). Counts come from the live config so
 *  a tenth city or an eighth ride can never leave the paywall stale. Owner
 *  rule: buying does NOT unlock the rides (winning races does) — "TO UNLOCK"
 *  and "EARN" keep the copy honest. */
const FEATURES: readonly {
  icon: string;
  accent: string;
  title: readonly [string, string];
  sub: readonly [string, string];
}[] = [
  {
    icon: MENU_ICONS.globe,
    accent: '#ffd34a',
    title: [`RACE ${CITIES.length}`, 'ICONIC CITIES'],
    sub: ['FROM NEW YORK', 'TO TOKYO'],
  },
  {
    icon: MENU_ICONS.car,
    accent: '#38e1ff',
    title: [`${VEHICLE_ROSTER.length} RIDES`, 'TO UNLOCK'],
    sub: ['EARN AND CUSTOMIZE', 'UNIQUE CARS'],
  },
  {
    icon: MENU_ICONS.people,
    accent: '#a77bff',
    title: ['ONLINE', 'MULTIPLAYER'],
    sub: ['RACE YOUR FRIENDS', 'ANYTIME, ANYWHERE'],
  },
];

/** The four disclosure points, tightened but not weakened (M123):
 *  license-not-ownership · server-dependent online w/ advance notice ·
 *  revocation · the full-terms link (rendered separately). */
const DISCLOSURE_LINES: readonly string[] = [
  'A license to play Dribble Racer, not ownership of the game.',
  'Online multiplayer is server dependent and may be discontinued in the future. If that happens, online play ends for everyone, including buyers. We will give advance notice.',
  'Your license can be revoked if these terms are broken, or if we lose the rights to something in the game.',
];

type Face = 'pitch' | 'busy' | 'confirming' | 'success' | 'error';

export class PurchaseScreen {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly backBtn: HTMLButtonElement;
  private face: Face = 'pitch';
  private errorMsg = '';
  private info: PurchaseInfo | null = null;
  private state: AccountState | null = null;
  /** True when THIS visit witnessed the purchase land (confirming → owned):
   *  the success face celebrates. An already-owned visitor stays calm. */
  private celebrate = false;
  /** M101 seam: we added body.rotate-dismissed ourselves (restored on hide). */
  private addedRotateDismiss = false;

  constructor(
    parent: HTMLElement,
    private cb: PurchaseCallbacks,
  ) {
    // M147: the whole screen (every face) sits on the owner's rooftop photo.
    this.root = el('div', 'screen screen-purchase screen-cine buy-photo', parent);
    this.root.style.display = 'none';
    this.panel = el('div', 'acct-panel buy-panel', this.root);
    this.backBtn = backButton(this.root, () => {
      if (this.face === 'busy' || this.face === 'confirming') return;
      this.cb.onBack?.();
    });
  }

  get isVisible(): boolean {
    return this.root.style.display !== 'none';
  }

  show(info: PurchaseInfo, state: AccountState): void {
    this.info = info;
    this.state = state;
    this.errorMsg = '';
    this.celebrate = info.owned && !!info.confirming;
    this.face = info.owned ? 'success' : info.confirming ? 'confirming' : 'pitch';
    // Portrait phones (the M101 onboarding seam): the rotate nudge must not
    // paint over the paywall CTA — this screen is portrait-capable by design.
    // hide() restores it, so racing gets the nudge back automatically.
    if (!document.body.classList.contains('rotate-dismissed')) {
      document.body.classList.add('rotate-dismissed');
      this.addedRotateDismiss = true;
    }
    this.render();
    this.root.style.display = '';
  }

  /** A sync landing while the screen is up repaints in place: confirming flips
   *  to the success celebration the moment `owned` turns true (the account
   *  sync drives it), and a sign-in state change re-derives the CTA stack —
   *  an owner must never sit on a paywall. */
  refresh(info: PurchaseInfo, state: AccountState): void {
    if (!this.isVisible) return;
    this.info = info;
    this.state = state;
    if (info.owned) {
      if (this.face !== 'success') {
        this.celebrate = this.face === 'confirming' || this.face === 'busy' || !!info.confirming;
        this.face = 'success';
      }
    } else if (info.confirming && (this.face === 'pitch' || this.face === 'busy')) {
      this.face = 'confirming';
    }
    this.render();
  }

  hide(): void {
    if (this.addedRotateDismiss) {
      document.body.classList.remove('rotate-dismissed');
      this.addedRotateDismiss = false;
    }
    this.root.style.display = 'none';
  }

  // ---- render --------------------------------------------------------------

  private render(): void {
    const info = this.info;
    if (!info) return;
    this.panel.replaceChildren();
    this.panel.classList.toggle('buy-panel-pitch', this.face === 'pitch');
    this.root.classList.toggle('buy-pitch', this.face === 'pitch');
    // The chevron shows whenever a way back exists, except mid-flight (the
    // Stripe hand-off and the grant confirmation own their own exits). Because
    // backNav resolves edge swipes by VISIBLE back buttons, hiding it also
    // disables the swipe for those beats (M114b).
    const chevron = !!this.cb.onBack && this.face !== 'busy' && this.face !== 'confirming';
    this.backBtn.style.display = chevron ? '' : 'none';

    if (this.face === 'success') this.renderSuccess();
    else if (this.face === 'busy') this.renderBusy();
    else if (this.face === 'confirming') this.renderConfirming();
    else if (this.face === 'error') this.renderError();
    else this.renderPitch(info);
  }

  /** Shared editorial head: gold rule-flanked kicker over a display title. */
  private addHead(
    parent: HTMLElement,
    title: string,
    opts: { kicker?: string; sub?: string; gold?: boolean } = {},
  ): void {
    const head = el('div', 'acct-head', parent);
    const kicker = el('div', 'lb-kicker', head);
    el('span', 'mhead-rule', kicker);
    el('span', 'lb-kicker-text', kicker).textContent = opts.kicker ?? 'DRIBBLE RACER';
    el('span', 'mhead-rule', kicker);
    const t = el('div', opts.gold ? 'lb-title buy-title-gold' : 'lb-title', head);
    t.textContent = title;
    t.dataset.text = title;
    if (opts.sub) {
      el('div', opts.gold ? 'lb-sub buy-sub-gold' : 'lb-sub', head).textContent = opts.sub;
    }
  }

  /** The on-brand loading treatment (busy + confirming): a gold ring. */
  private addSpinner(parent: HTMLElement): void {
    el('div', 'buy-spin', parent);
  }

  // ---- pitch ---------------------------------------------------------------

  private renderPitch(info: PurchaseInfo): void {
    // M147: one fixed sell for every route in (the owner's reference art) —
    // the 'cap' / 'return' / feature contexts no longer change the headline.
    const hero = el('div', 'buy-hero', this.panel);
    // M148: the owner's logo art in place of the text kicker.
    brandLogo(hero, 'buy-brandlogo', { small: true });
    const h = el('h1', 'buy-headline', hero);
    el('span', 'buy-hl buy-hl-chrome', h).textContent = HEADLINE[0];
    el('span', 'buy-hl buy-hl-gold', h).textContent = HEADLINE[1];

    // The three feature columns, hairline dividers between them.
    const feats = el('div', 'buy-feats', this.panel);
    for (const f of FEATURES) {
      const col = el('div', 'buy-feat', feats);
      col.style.setProperty('--feat-accent', f.accent);
      el('span', 'buy-feat-ic', col).innerHTML = f.icon; // static SVG constants
      const t = el('div', 'buy-feat-title', col);
      for (const line of f.title) el('span', '', t).textContent = line;
      const sub = el('div', 'buy-feat-sub', col);
      for (const line of f.sub) el('span', '', sub).textContent = line;
    }

    // The price lockup: the big gold $ over the one-time promise.
    const deal = el('div', 'buy-deal', this.panel);
    const price = el('div', 'buy-price', deal);
    price.textContent = info.price;
    el('div', 'buy-once', deal).textContent = ONE_TIME_LINE;
    el('div', 'buy-promise', deal).textContent = PROMISE_LINE;

    if (info.refundedOn) {
      // Calm, not an error state (owner decision) — they are welcome back.
      el('div', 'buy-refunded', this.panel).textContent =
        `Your purchase was refunded on ${info.refundedOn}. You are welcome back any time.`;
    }

    // ---- the CTA stack -----------------------------------------------------
    const actions = el('div', 'buy-actions', this.panel);
    const signedIn = this.state?.status === 'signedIn';
    // The M110 rule: buying needs a Racer ID — the entitlement lives on the
    // account and follows the player across every device. Signed out, the
    // CTA says so; signed in, it is the plain verb (the price sits above).
    const label = signedIn ? 'BUY NOW' : 'CREATE RACER ID & BUY NOW';
    const cta = el('button', 'buy-hero-cta', actions);
    cta.type = 'button';
    cta.setAttribute('aria-label', `${label}, ${info.price}`);
    el('span', 'bhc-ic', cta).innerHTML = CROWN_SVG;
    el('span', 'bhc-label', cta).textContent = label;
    el('span', 'bhc-chev', cta).innerHTML = CHEV_SVG;
    el('span', 'bhc-sheen', cta);
    cta.addEventListener('click', () => {
      sfxUiTick();
      if (signedIn) void this.startBuy();
      else this.cb.onSignIn();
    });

    // COMPLIANCE: the one-line license notice, DIRECTLY under the primary CTA
    // (owner-approved placement). Small but legible — never below 12px.
    const lic = el('div', 'buy-lic-line', actions);
    el('span', 'buy-lic-text', lic).textContent =
      "You're buying a license to play Dribble Racer · ";
    const licLink = el('button', 'buy-lic-link', lic);
    licLink.type = 'button';
    licLink.textContent = 'Full terms';
    licLink.addEventListener('click', () => this.cb.onTerms());

    if (!signedIn) {
      // A paid player on a new phone is a first-class persona — a real glass
      // button with a gold rim, not a footnote ghost.
      const si = el('button', 'buy-hero-signin', actions);
      si.type = 'button';
      el('span', 'bhs-label', si).textContent = 'ALREADY OWN IT? SIGN IN';
      el('span', 'bhs-chev', si).innerHTML = CHEV_SVG;
      si.addEventListener('click', () => {
        sfxUiTick();
        this.cb.onSignIn();
      });
    }

    // M147 (owner reference art): no free-race button here any more. The
    // title's DEMO RACE card always plays the demo (startTeaserRace bypasses
    // the soft cap), so nobody is walled — the back chevron leads there.

    // COMPLIANCE: the full disclosure block, below the buttons. Its own
    // bordered, visually distinct treatment — always visible, never collapsed.
    this.renderDisclosure(this.panel);
  }

  /** The AB 2426 / Maryland HB 208 notice — see the header comment for why
   *  this stays a distinct bordered block and carries no checkbox. */
  private renderDisclosure(parent: HTMLElement): void {
    const box = el('div', 'buy-license', parent);
    el('div', 'buy-license-head', box).textContent = 'What you are buying';
    for (const line of DISCLOSURE_LINES) {
      el('p', 'buy-license-p', box).textContent = line;
    }
    const link = el('button', 'buy-license-link', box);
    link.type = 'button';
    link.textContent = 'Read the full Terms of Sale and License';
    link.addEventListener('click', () => this.cb.onTerms());
  }

  // ---- the other faces -----------------------------------------------------

  private renderBusy(): void {
    const wrap = el('div', 'buy-quiet', this.panel);
    this.addHead(wrap, 'ONE MOMENT');
    this.addSpinner(wrap);
    el('div', 'buy-note', wrap).textContent = 'Taking you to secure checkout...';
  }

  private renderConfirming(): void {
    const wrap = el('div', 'buy-quiet', this.panel);
    this.addHead(wrap, 'CONFIRMING YOUR PURCHASE');
    this.addSpinner(wrap);
    // Never an error for slowness — calm, premium, self-resolving.
    el('div', 'buy-note', wrap).textContent =
      'This takes a few seconds. The game unlocks automatically.';
    if (this.cb.onBack) {
      btn('BACK TO THE GAME', 'btn btn-ghost buy-quiet-back', wrap, () => this.cb.onBack?.());
    }
  }

  private renderSuccess(): void {
    const wrap = el('div', 'buy-success', this.panel);
    if (this.celebrate) wrap.classList.add('buy-celebrate');

    // The trophy under a slow gold ray burst — the tour-finale language.
    const hero = el('div', 'buy-burst-wrap', wrap);
    el('div', 'buy-burst', hero);
    const cup = document.createElement('img');
    cup.className = 'buy-success-cup';
    cup.alt = '';
    cup.src = `${import.meta.env.BASE_URL}lobby/trophy.webp`;
    cup.addEventListener('error', () => cup.remove());
    hero.appendChild(cup);

    if (this.celebrate) {
      this.addHead(wrap, "YOU'RE IN", {
        kicker: 'PURCHASE COMPLETE',
        sub: 'THE WORLD TOUR IS YOURS',
        gold: true,
      });
    } else {
      // The already-owned visitor: same face, calmer voice.
      this.addHead(wrap, "YOU'RE IN", { sub: 'FULL GAME · OWNED', gold: true });
      el('div', 'buy-note', wrap).textContent =
        'Your Racer ID owns Dribble Racer. Sign in on any device to play.';
    }

    if (this.cb.onPlay) {
      btn('START RACING', 'btn btn-gold buy-cta', wrap, () => this.cb.onPlay?.());
    } else if (this.cb.onBack) {
      btn('CONTINUE', 'btn btn-gold buy-cta', wrap, () => this.cb.onBack?.());
    }
  }

  private renderError(): void {
    const wrap = el('div', 'buy-quiet', this.panel);
    this.addHead(wrap, 'CHECKOUT UNAVAILABLE');
    el('div', 'buy-note', wrap).textContent =
      this.errorMsg || 'We could not start checkout. Nothing was charged.';
    btn('TRY AGAIN', 'btn btn-gold buy-cta', wrap, () => {
      this.face = 'pitch';
      this.errorMsg = '';
      this.render();
    });
    if (this.cb.onTeaser) {
      btn('PLAY THE DEMO RACE', 'btn btn-ghost', wrap, () => this.cb.onTeaser?.());
    }
  }

  // ---- actions -------------------------------------------------------------

  private async startBuy(): Promise<void> {
    if (this.face === 'busy') return;
    this.face = 'busy';
    this.render();
    let err: string | null;
    try {
      err = await this.cb.onBuy();
    } catch {
      err = 'We could not reach checkout. Nothing was charged.';
    }
    // A null result means the handler navigated away to Stripe — leave the busy
    // face up rather than flashing the pitch behind the redirect.
    if (err === null) return;
    this.errorMsg = err;
    this.face = 'error';
    this.render();
  }
}
